import { useCallback, useEffect, useRef, useState } from "react";
import { AppState, AppStateStatus } from "react-native";

import {
  SESSION_IDLE_TIMEOUT_MS,
  SESSION_WARNING_BEFORE_TIMEOUT_MS,
} from "@/domain/constants";

const COUNTDOWN_INTERVAL_MS = 1000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

type UseIdleTimerOptionsType = {
  onTimeout: () => void;
  onManualLogout?: () => void;
};

type UseIdleTimerReturnType = {
  isWarningVisible: boolean;
  remainingSeconds: number;
  onStayLoggedIn: () => void;
  onLogoutNow: () => void;
  onUserActivity: () => void;
};

export const useIdleTimer = ({
  onTimeout,
  onManualLogout,
}: UseIdleTimerOptionsType): UseIdleTimerReturnType => {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    SESSION_WARNING_BEFORE_TIMEOUT_MS / 1000,
  );

  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const onManualLogoutRef = useRef(onManualLogout);
  onManualLogoutRef.current = onManualLogout;

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isWarningVisibleRef = useRef(false);
  const backgroundTimestampRef = useRef<number | null>(null);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    setRemainingSeconds(SESSION_WARNING_BEFORE_TIMEOUT_MS / 1000);
    setIsWarningVisible(true);
    isWarningVisibleRef.current = true;

    countdownTimerRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        const next = prev - 1;
        if (next <= 0) {
          clearAllTimers();
          setIsWarningVisible(false);
          isWarningVisibleRef.current = false;
          onTimeoutRef.current();
          return 0;
        }
        return next;
      });
    }, COUNTDOWN_INTERVAL_MS);
  }, [clearAllTimers]);

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    setIsWarningVisible(false);
    isWarningVisibleRef.current = false;
    setRemainingSeconds(SESSION_WARNING_BEFORE_TIMEOUT_MS / 1000);

    idleTimerRef.current = setTimeout(() => {
      startCountdown();
    }, SESSION_IDLE_TIMEOUT_MS - SESSION_WARNING_BEFORE_TIMEOUT_MS);
  }, [clearAllTimers, startCountdown]);

  const onStayLoggedIn = useCallback(() => {
    startIdleTimer();
  }, [startIdleTimer]);

  const onLogoutNow = useCallback(() => {
    clearAllTimers();
    setIsWarningVisible(false);
    isWarningVisibleRef.current = false;
    if (onManualLogoutRef.current) {
      onManualLogoutRef.current();
    } else {
      onTimeoutRef.current();
    }
  }, [clearAllTimers]);

  const handleUserActivity = useCallback(() => {
    if (!isWarningVisibleRef.current) {
      startIdleTimer();
    }
  }, [startIdleTimer]);
  useEffect(() => {
    if (typeof document !== "undefined") {
      for (const event of ACTIVITY_EVENTS) {
        document.addEventListener(event, handleUserActivity, { passive: true });
      }
    }

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState === "background" || nextAppState === "inactive") {
        backgroundTimestampRef.current = Date.now();
      } else if (nextAppState === "active") {
        if (backgroundTimestampRef.current) {
          const elapsed = Date.now() - backgroundTimestampRef.current;
          backgroundTimestampRef.current = null;

          if (elapsed >= SESSION_IDLE_TIMEOUT_MS) {
            clearAllTimers();
            onTimeoutRef.current();
            return;
          }
        }

        if (!isWarningVisibleRef.current) {
          startIdleTimer();
        }
      }
    };

    const appStateSubscription = AppState.addEventListener(
      "change",
      handleAppStateChange,
    );

    startIdleTimer();

    return () => {
      if (typeof document !== "undefined") {
        for (const event of ACTIVITY_EVENTS) {
          document.removeEventListener(event, handleUserActivity);
        }
      }
      appStateSubscription.remove();
      clearAllTimers();
    };
  }, [startIdleTimer, clearAllTimers, handleUserActivity]);

  return {
    isWarningVisible,
    remainingSeconds,
    onStayLoggedIn,
    onLogoutNow,
    onUserActivity: handleUserActivity,
  };
};
